import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStatiquesTextes, TexteRiche } from '../hooks/useFormTextes';
import { ChevronDown } from 'lucide-react';

const SectionTitle = ({ children, color }) => (
  <div className={`${color || 'bg-[#1E3A5F]'} text-white px-6 py-4 rounded-t-2xl`}>
    <h2 className="font-bold text-lg">{children}</h2>
  </div>
);

const INFO_SECTIONS = [
  { id: 'section-comment-inscrire', label: "Comment s'inscrire", short: "S'inscrire" },
  { id: 'section-voyageurs-solo', label: 'Voyageurs solo', short: 'Solo' },
  { id: 'section-points-depart', label: 'Points de départ', short: 'Départs' },
  { id: 'section-places-bus', label: 'Places de car : à vous de choisir !', short: 'Places de car' },
  { id: 'section-accompagnement', label: 'Accompagnement administratif', short: 'Accompagnement' },
  { id: 'section-adhesion-info', label: 'Adhesion annuelle', short: 'Adhesion' },
  { id: 'section-assurance-info', label: 'Assurance multirisque', short: 'Assurance' },
  { id: 'section-modes-paiement', label: 'Modes de paiement', short: 'Paiement' },
  { id: 'section-echeancier', label: 'Echeancier', short: 'Echeancier' },
  { id: 'section-avant-reserver', label: 'Avant de réserver', short: 'Avant de réserver' },
  { id: 'section-frais-annulation', label: "Frais d'annulation", short: 'Annulation' },
];

const InformationsAdministratives = () => {
  const T = useStatiquesTextes(); // Iter 770 — textes éditables (Textes du site → Pages du site)
  const [activeSection, setActiveSection] = useState('');
  const [showSticky, setShowSticky] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 }
    );
    INFO_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setShowSticky(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = window.innerWidth < 1024 ? 120 : 140;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F4EF] to-white">
      <section className="bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white py-16" ref={headerRef}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 uppercase tracking-wide">INFORMATIONS PRATIQUES</h1>
          <p className="text-base sm:text-lg opacity-90">Tout ce qu'il faut savoir avant de s'inscrire</p>
        </div>
      </section>

      {/* Sommaire - grille cliquable */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <h3 className="text-sm font-bold text-[#1E3A5F] uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-[#5BC5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
            Sur cette page
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {INFO_SECTIONS.map(({ id, label }, i) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-left group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F0FAFB] transition-colors"
                data-testid={`sommaire-${id}`}
              >
                <span className="w-5 h-5 rounded-full bg-[#5BC5D4]/10 text-[#5BC5D4] text-[10px] font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-[#5BC5D4] group-hover:text-white transition-colors">
                  {i + 1}
                </span>
                <span className="text-sm text-gray-700 group-hover:text-[#1E3A5F] font-medium transition-colors">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky sub-menu */}
        <div className={`fixed top-[68px] lg:top-[100px] left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100 transition-all duration-300 ${showSticky ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`} data-testid="info-sticky-nav">
          <div className="max-w-6xl mx-auto px-4 flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
            {INFO_SECTIONS.map(({ id, short }) => (
              <button key={id} onClick={() => scrollTo(id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeSection === id
                    ? 'bg-[#79c9ea] text-white shadow-sm'
                    : 'text-gray-500 hover:bg-[#79c9ea]/10 hover:text-[#4ba8c0]'
                }`}>
                {short}
              </button>
            ))}
          </div>
        </div>
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="info-admin-page">

        {/* COMMENT S'INSCRIRE */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-comment-inscrire" data-testid="section-comment-inscrire">
          <SectionTitle color="bg-[#79c9ea]">Comment s'inscrire ?</SectionTitle>
          <div className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 leading-relaxed">
              <p className="font-bold text-sm text-[#1E3A5F] mb-2">{<TexteRiche texte={T('ip_intro_titre')} />}</p>
              <p>{<TexteRiche texte={T('ip_intro')} />}</p>
            </div>

            <div className="bg-[#5BC5D4]/10 border-2 border-[#5BC5D4] rounded-xl p-5">
              <h3 className="font-bold text-[#1E3A5F] text-base mb-3">Les 3 étapes</h3>
              <ol className="text-xs text-gray-700 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="bg-[#5BC5D4] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <div>
                    <span className="font-bold text-[#1E3A5F]">Remplissez le formulaire</span>
                    <p className="text-gray-500 mt-0.5">{<TexteRiche texte={T('ip_etape1')} />}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#5BC5D4] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <div>
                    <span className="font-bold text-[#1E3A5F]">Choisissez votre mode de règlement</span>
                    <p className="text-gray-500 mt-0.5">{<TexteRiche texte={T('ip_etape2')} />}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#5BC5D4] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <div>
                    <span className="font-bold text-[#1E3A5F]">Réglez sous 24 heures</span>
                    <p className="text-gray-500 mt-0.5">{<TexteRiche texte={T('ip_etape3')} />}</p>
                  </div>
                </li>
              </ol>
              <div className="mt-4">
                <Link to="/inscription" className="block w-full bg-[#5BC5D4] text-white text-center px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-[#4AB4C3] transition" data-testid="link-formulaire">
                  Formulaire d'inscription
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 100% en ligne */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <h3 className="font-bold text-[#1E3A5F] text-sm mb-2">100 % à distance</h3>
                <p className="text-xs text-gray-600 mb-2">{<TexteRiche texte={T('ip_distance_1')} />}</p>
                <p className="text-[11px] text-gray-500 mb-3">{<TexteRiche texte={T('ip_distance_2')} />}</p>
                <Link to="/moi?payer=1" className="block w-full bg-[#1E3A5F] text-white text-center px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-[#16304F] transition" data-testid="link-payer">
                  Régler depuis mon espace
                </Link>
              </div>

              {/* Rendez-vous */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <h3 className="font-bold text-[#1E3A5F] text-sm mb-2">En rendez-vous (10 min)</h3>
                <p className="text-xs text-gray-600 mb-2">{<TexteRiche texte={T('ip_rdv_1')} />}</p>
                <p className="text-[11px] text-gray-500 mb-3">{<TexteRiche texte={T('ip_rdv_2')} />}</p>
                <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800 mb-3">
                  <p className="font-bold mb-1">📍 Permanences au Decathlon de Bron</p>
                  <p className="mb-1">{<TexteRiche texte={T('ip_permanence_adresse')} />}</p>
                  <ul className="space-y-0.5">
                    <li>&#8226; <strong>Samedi matin</strong></li>
                    <li>&#8226; <strong>Mercredi en fin de journée</strong></li>
                  </ul>
                  <p className="text-gray-500 mt-1">Sur rendez-vous uniquement.</p>
                </div>
                <Link to="/moi?rdv=1" onClick={() => window.scrollTo(0, 0)} className="block w-full bg-[#79c9ea] text-white text-center px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-[#5ba8c8] transition" data-testid="link-rdv">
                  Prendre rendez-vous
                </Link>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 leading-relaxed">
              <p>{<TexteRiche texte={T('ip_important')} />}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 leading-relaxed">
              <p>{<TexteRiche texte={T('ip_aide')} />}</p>
            </div>
          </div>
        </section>

        {/* VOYAGEURS SOLO */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-voyageurs-solo" data-testid="section-voyageurs-solo">
          <SectionTitle color="bg-[#E67E22]">Vous voyagez seul(e) ? Bonne nouvelle !</SectionTitle>
          <div className="p-6 space-y-5">
            <p className="text-sm text-gray-700 leading-relaxed">
              Vous êtes <strong>nombreux(ses) à voyager solo chez nous</strong>, et nous avons presque toujours une solution adaptée pour vous !
              Tous nos tarifs sont établis sur la base d'une <strong>chambre double (2 personnes)</strong>.
              Il vous suffit simplement de choisir l'option qui vous convient le mieux :
            </p>

            {/* Option 1 */}
            <div className="bg-gradient-to-r from-[#FFF8E1] to-[#FFF3CD] border-2 border-[#E67E22] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#E67E22] text-white text-xs font-bold px-2.5 py-1 rounded-full">Option 1</span>
                <span className="bg-[#27AE60] text-white text-xs font-bold px-2.5 py-1 rounded-full">La plus choisie !</span>
              </div>
              <h4 className="font-bold text-[#1E3A5F] mb-2">Partager avec un(e) autre voyageur(se) solo</h4>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Vous acceptez de partager votre chambre avec un(e) autre participant(e) voyageant également seul(e).
                C'est l'option la plus fréquente dans nos groupes, et nous trouvons <strong>très régulièrement une correspondance</strong>, notamment entre voyageuses !
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <span className="text-green-600 font-bold text-lg flex-shrink-0">&#10003;</span>
                  <p className="text-sm text-gray-700">Un(e) autre voyageur(se) solo est disponible = <strong>inscription confirmée immédiatement !</strong></p>
                </div>
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="text-amber-500 font-bold text-lg flex-shrink-0">&#9202;</span>
                  <p className="text-sm text-gray-700">Pas encore de correspondance = <strong>vous êtes placé(e) sur liste d'attente</strong> et nous vous contactons dès qu'une solution se présente.</p>
                </div>
              </div>
            </div>

            {/* Option 2 */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#5BC5D4] text-white text-xs font-bold px-2.5 py-1 rounded-full">Option 2</span>
              </div>
              <h4 className="font-bold text-[#1E3A5F] mb-2">Partager avec une personne de votre entourage</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Vous souhaitez partager votre chambre avec un(e) ami(e) ou un(e) proche, déjà inscrit(e) ou qui s'inscrit en même temps que vous.
                Communiquez-nous simplement ses <strong>nom et prénom</strong> au moment de votre prise de contact.
              </p>
            </div>

            {/* Option 3 */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#5BC5D4] text-white text-xs font-bold px-2.5 py-1 rounded-full">Option 3</span>
              </div>
              <h4 className="font-bold text-[#1E3A5F] mb-2">Chambre individuelle</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Vous préférez une chambre rien que pour vous.
                Un <strong>supplément chambre individuelle</strong> sera appliqué, <strong>sous réserve de disponibilité</strong>.
                Nous vous confirmons la disponibilité dès réception de votre demande.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-4 text-center">
              <p className="text-sm text-gray-700 font-medium mb-3">
                Prêt(e) à nous rejoindre ? Contactez-nous en nous indiquant votre choix, notre équipe s'occupe du reste !
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="https://wa.me/33660416218" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#1DA851] transition" data-testid="solo-whatsapp-btn">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  06 60 41 62 18 — WhatsApp
                </a>
                <a href="mailto:rhoneamitie@gmail.com"
                  className="inline-flex items-center justify-center gap-2 border border-[#5BC5D4] text-[#5BC5D4] px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#5BC5D4]/10 transition" data-testid="solo-email-btn">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  rhoneamitie@gmail.com
                </a>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 italic">
              Voyager seul(e), oui, mais toujours bien entouré(e) !
            </p>
          </div>
        </section>

        {/* POINTS DE DÉPART */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-points-depart" data-testid="section-points-depart">
          <SectionTitle color="bg-[#2C3E50]">Nos points de départ</SectionTitle>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Car */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/></svg>
                  </div>
                  <h4 className="font-bold text-[#1E3A5F]">Voyages en autocar</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                    <span><strong>Saint-Étienne</strong> — Devant la gare Châteaucreux</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                    <span><strong>Saint-Priest</strong> — Arrêts TCL derrière la mairie — Rue Joan Miro, 69800</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                    <span><strong>Villeurbanne</strong> — Piscine de Cusset — Avenue Marcel Cerdan, 69100</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mt-3 bg-amber-50 p-2.5 rounded-lg">
                  Pour l'Espagne, nous pouvons récupérer des participants sur des aires d'autoroute de l'A7 et l'A9
                  entre Lyon et Montpellier (Valence, Orange, Montélimar...).
                </p>
                {/* Iter 156b (Kamel) — Lien vers la Charte de placement bus */}
                <a
                  href="/charte-placement-bus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center justify-between gap-2 bg-[#1E3A5F] hover:bg-[#274d7a] text-white rounded-lg px-3 py-2.5 transition group"
                  data-testid="infos-pratiques-charte-bus-link"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <span className="text-base">📜</span>
                    Charte de placement en autocar
                  </span>
                  <span className="text-xs opacity-80 group-hover:opacity-100">Lire →</span>
                </a>
              </div>

              {/* Avion */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                  </div>
                  <h4 className="font-bold text-[#1E3A5F]">Voyages en avion</h4>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                  <span><strong>Aéroport de Lyon Saint-Exupéry</strong></span>
                </div>
                <p className="text-xs text-gray-500 mt-3 bg-blue-50 p-2.5 rounded-lg">
                  Les horaires de départ précis (heure, lieu exact de rendez-vous) sont systématiquement communiqués dans le programme,
                  puis rappelés quelques jours avant, principalement via WhatsApp.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ACCOMPAGNEMENT ADMINISTRATIF ET SANITAIRE */}
        {/* 🚌 PLACES DE CAR — CHOIX DU SIÈGE GRATUIT (révolution 2026 !) */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-places-bus" data-testid="section-places-bus">
          <SectionTitle color="bg-[#1E3A5F]">🚌 Places de car : à vous de choisir — gratuitement !</SectionTitle>
          <div className="p-6 space-y-3 text-sm text-gray-700 leading-relaxed">
            <p className="font-bold text-[#1E3A5F]">
              Une grande nouveauté après 30 ans : sur tous nos voyages en autocar, c'est désormais
              VOUS qui choisissez vos places sur le plan du car — gratuitement, depuis votre téléphone ou ordinateur.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="font-bold text-sky-900 mb-1">⏱️ Premier arrivé, premier servi</p>
                <p className="text-xs text-sky-800">Dès votre <strong>premier paiement</strong>, vous recevez votre lien personnel « Mon Bus » (par email et WhatsApp, aussi dans votre Espace Adhérent). Plus vous choisissez tôt, plus vous avez de choix !</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="font-bold text-emerald-900 mb-1">👨‍👩‍👧‍👦 Vous voyagez à plusieurs familles ?</p>
                <p className="text-xs text-emerald-800">Inscrivez-vous et payez <strong>en même temps</strong>, puis coordonnez-vous pour choisir vos places dès réception du lien : c'est la meilleure garantie d'être <strong>côte à côte</strong>.</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-bold text-amber-900 mb-1">🔄 Modifiable jusqu'au départ</p>
                <p className="text-xs text-amber-800">Vous pouvez changer vos places à tout moment avant le départ, selon les disponibilités. En cas d'annulation, les places se libèrent automatiquement.</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="font-bold text-red-900 mb-1">⚠️ Sans choix de votre part</p>
                <p className="text-xs text-red-800">Une place vous sera attribuée automatiquement avant le départ — attribution définitive et <strong>non négociable</strong>.</p>
              </div>
            </div>
            <div className="bg-[#1E3A5F] text-white rounded-xl p-4">
              <p className="font-bold mb-1">🎫 Et le jour du départ ? Zéro stress !</p>
              <p className="text-xs leading-relaxed text-sky-100">
                Vous recevez votre billet d'embarquement avec un QR code : à la montée dans le car,
                l'accompagnateur le scanne en une seconde, et vos places — déjà numérotées et réservées
                à votre nom — vous attendent. Plus de course aux sièges, plus de discussions : chacun
                s'installe tranquillement à SA place. Et tout cela, entièrement gratuit !
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Tous les détails dans la <a href="/charte-placement-bus" className="underline font-semibold text-[#1E3A5F]">charte de placement</a>.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-accompagnement" data-testid="section-accompagnement">
          <SectionTitle>Accompagnement administratif et sanitaire</SectionTitle>
          <div className="p-6 space-y-5">
            <p className="text-sm text-gray-600">{<TexteRiche texte={T('ip_accomp_intro')} />}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-[#1E3A5F] text-sm mb-3">Formalités administratives</h3>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Vérifier la validité de votre carte d'identité ou passeport</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Comprendre si un visa ou autorisation (ESTA, e-visa...) est nécessaire</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Remplir ensemble les formulaires en ligne, pas à pas</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Rassembler les documents demandés par les compagnies</span></li>
                </ul>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h3 className="font-bold text-[#1E3A5F] text-sm mb-3">Conseils sanitaires</h3>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Repérer les vaccins conseillés pour votre destination</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Conseils trousse à pharmacie, précautions alimentaires et climatiques</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Préparer les attestations et documents d'assurance</span></li>
                </ul>
              </div>
            </div>
            <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-4 text-xs text-gray-700 leading-relaxed">
              <p>{<TexteRiche texte={T('ip_accomp_fin')} />}</p>
            </div>
          </div>
        </section>

        {/* ADHÉSION ANNUELLE */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-adhesion-info" data-testid="section-adhesion-info">
          <SectionTitle>Adhésion annuelle</SectionTitle>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
              <p>{<TexteRiche texte={T('ip_adhesion')} />}</p>
              <p className="mt-2 font-semibold text-[#1E3A5F]">{<TexteRiche texte={T('ip_adhesion_journee')} />}</p>
            </div>
          </div>
        </section>

        {/* ASSURANCE MULTIRISQUE */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-assurance-info" data-testid="section-assurance-info">
          <SectionTitle color="bg-[#5BC5D4]">Protégez votre voyage avec notre assurance</SectionTitle>
          <div className="p-6 space-y-5">
            <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
              <p className="font-bold text-[#1E3A5F] mb-1">En partenariat avec Groupama</p>
              <p>{<TexteRiche texte={T('ip_assurance_1')} />}</p>
              <p className="mt-2">{<TexteRiche texte={T('ip_assurance_2')} />}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Garantie Annulation */}
              <div className="border-2 border-red-200 rounded-xl overflow-hidden">
                <div className="bg-red-50 px-4 py-3">
                  <h3 className="font-bold text-red-800 text-sm">Garantie Annulation</h3>
                </div>
                <div className="p-4 text-xs text-gray-700 space-y-2">
                  <p>Remboursement des sommes engagées si vous devez annuler votre voyage avant le départ pour l'un des motifs couverts :</p>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Maladie grave, accident ou décès (vous, votre famille proche)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Complications de grossesse</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Licenciement économique</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Refus de visa, vol de documents (48h avant départ)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span>Destruction de locaux (&gt;50%), convocation judiciaire</span></li>
                  </ul>
                  <p className="text-red-700 font-semibold mt-2"><strong>Franchise :</strong> 10% du montant (min. 40 €, max. 100 €)</p>
                </div>
              </div>

              {/* Autres Garanties */}
              <div className="border-2 border-blue-200 rounded-xl overflow-hidden">
                <div className="bg-blue-50 px-4 py-3">
                  <h3 className="font-bold text-blue-800 text-sm">Autres Garanties</h3>
                </div>
                <div className="p-4 text-xs text-gray-700 space-y-1">
                  <p className="mb-2">Couverture complète pendant votre voyage :</p>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Assistance rapatriement 24h/24</strong> en cas de maladie ou accident</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Frais médicaux à l'étranger</strong> (hospitalisation, soins, dentaire d'urgence)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Perte ou vol de bagages</strong> (remboursement)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Interruption de voyage</strong> (remboursement frais non consommés)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Protection accident corporel</strong> (capital décès/invalidité)</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500 flex-shrink-0">&#10003;</span><span><strong>Avance de fonds</strong> en cas de perte ou vol</span></li>
                  </ul>
                  <p className="mt-2 font-bold text-[#1E3A5F]">Assistance 24h/24, 7j/7 : 00 33 1 45 16 66 05</p>
                </div>
              </div>
            </div>

            {/* Documents téléchargeables */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5" data-testid="documents-assurance">
              <h3 className="font-bold text-[#1E3A5F] text-sm mb-3">Documents à télécharger</h3>
              <p className="text-xs text-gray-600 mb-4">Consultez le détail complet des garanties et conditions d'assurance :</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://customer-assets.emergentagent.com/job_fr-application/artifacts/7qse8mat_GARANTIES_ANNULATION_AVANT_DEPART.pdf"
                  target="_blank" rel="noopener noreferrer" data-testid="download-garanties-annulation"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1E3A5F] text-white px-4 py-3 rounded-lg font-bold text-sm hover:bg-[#16304F] transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                  Garanties Annulation
                </a>
                <a href="https://customer-assets.emergentagent.com/job_fr-application/artifacts/d7mrj9bg_AUTRES_GARANTIES_DU_CONTRAT_D_ASSURANCE_MULTIRISQUES.pdf"
                  target="_blank" rel="noopener noreferrer" data-testid="download-autres-garanties"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#5BC5D4] text-white px-4 py-3 rounded-lg font-bold text-sm hover:bg-[#4AB4C3] transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                  Autres Garanties
                </a>
              </div>
            </div>

            {/* Important */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed space-y-1">
              <p className="font-bold">Important :</p>
              <ul className="space-y-1">
                <li>&#8226; L'assurance doit être souscrite <strong>au moment de l'inscription</strong></li>
                <li>&#8226; Le coût de l'assurance est calculé en fonction du montant de votre voyage</li>
                <li>&#8226; Certaines exclusions s'appliquent — consultez les documents pour les détails</li>
                <li>&#8226; En cas d'assistance, appelez <strong>immédiatement</strong> le numéro d'urgence</li>
              </ul>
            </div>
          </div>
        </section>

        {/* MODES DE PAIEMENT */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-modes-paiement" data-testid="section-modes-paiement">
          <SectionTitle>Modes de paiement acceptés</SectionTitle>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: '💳', title: 'Carte bancaire', desc: 'Paiement en ligne sécurisé', note: null },
                { icon: '🏦', title: 'Virement bancaire', desc: 'IBAN : FR76 1780 6002 8504 1611 8122 104', note: 'Au nom de : Rhone amitié. Envoyez-nous le justificatif + précisez le voyage' },
                { icon: '📝', title: 'Chèque bancaire', desc: 'Uniquement en rendez-vous', note: null },
                { icon: '💵', title: 'Espèces', desc: 'Uniquement en rendez-vous', note: null },
                { icon: '🎫', title: 'Chèques-vacances ANCV', desc: 'En rendez-vous ou par lettre recommandée', note: 'Contactez-nous par WhatsApp pour les modalités' },
                { icon: '🎟️', title: 'Avoir', desc: 'Suite annulation', note: 'Valable 2 ans sur un autre voyage Rhône Alpes Amitié' },
              ].map((m, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-4 hover:border-[#5BC5D4] transition">
                  <div className="text-2xl mb-2">{m.icon}</div>
                  <h3 className="font-bold text-[#1E3A5F] text-sm mb-1">{m.title}</h3>
                  <p className="text-xs text-gray-600">{m.desc}</p>
                  {m.note && <p className="text-xs text-amber-700 mt-1 font-medium">{m.note}</p>}
                </div>
              ))}
            </div>

            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 text-center">
              <h3 className="font-bold text-green-800 text-sm mb-1">Paiement en 10 fois sans frais</h3>
              <p className="text-xs text-green-700">Possibilité de payer en plusieurs fois avant le départ, sans frais supplémentaires !</p>
            </div>
          </div>
        </section>

        {/* ÉCHÉANCIER DE PAIEMENT */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-echeancier" data-testid="section-echeancier">
          <SectionTitle color="bg-[#79c9ea]">Échéancier de paiement</SectionTitle>
          <div className="p-6">
            <div className="space-y-0">
              {/* Step 1 */}
              <div className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">1</div>
                  <div className="w-0.5 h-full bg-[#79c9ea]/30 min-h-[40px]"></div>
                </div>
                <div className="pb-6">
                  <h3 className="font-bold text-[#1E3A5F] text-sm">À l'inscription</h3>
                  <p className="text-xs text-gray-700 mt-1"><strong>20 % minimum</strong> du forfait (50 % si moins de 6 mois avant le départ)</p>
                  <p className="text-xs text-gray-500 mt-0.5">+ Adhésion annuelle + Assurance si souscrite</p>
                </div>
              </div>
              {/* Step 2 */}
              <div className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">2</div>
                  <div className="w-0.5 h-full bg-[#79c9ea]/30 min-h-[40px]"></div>
                </div>
                <div className="pb-6">
                  <h3 className="font-bold text-[#1E3A5F] text-sm">6 mois avant le départ</h3>
                  <p className="text-xs text-gray-700 mt-1"><strong>50 % minimum</strong> du forfait doit être réglé</p>
                </div>
              </div>
              {/* Step 3 */}
              <div className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">3</div>
                </div>
                <div>
                  <h3 className="font-bold text-[#1E3A5F] text-sm">45 jours avant le départ</h3>
                  <p className="text-xs text-gray-700 mt-1"><strong>Solde total</strong> du voyage doit être réglé</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AVANT DE RÉSERVER — Méthode V.F.A.S.T. */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-avant-reserver" data-testid="section-avant-reserver">
          <SectionTitle color="bg-[#2C3E50]">Avant de réserver : la méthode V.F.A.S.T.</SectionTitle>
          <div className="p-6 space-y-5">
            <p className="text-sm text-gray-600 text-center mb-2">{<TexteRiche texte={T('ip_vfast_intro')} />}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white border rounded-xl p-5 border-l-4 border-[#F39C12] sm:col-span-2">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F39C12]/10 flex items-center justify-center text-xl font-black text-[#F39C12] flex-shrink-0">V</div>
                  <div className="flex-1">
                    <h4 className="text-base font-bold text-[#1E3A5F] mb-1">Voyage — la toute première chose à analyser</h4>
                    <p className="text-sm text-gray-600 mb-2">{<TexteRiche texte={T('ip_vfast_voyage')} />}</p>
                    <ul className="text-xs text-gray-600 space-y-1 list-disc ml-5">
                      <li><strong>Ce qui est inclus / ce qui ne l'est pas</strong> (formule petit-déjeuner ? repas du midi et du soir exclus ? excursions optionnelles ?)</li>
                      <li><strong>Lieux et horaires de départ</strong> (départ de nuit ? très tôt le matin ? comment rejoindre le lieu de rendez-vous si vous n'avez pas de voiture ni transport en commun ?)</li>
                      <li><strong>Type de chambre</strong> (chambre de 2, supplément individuel, lits jumeaux ou grand lit)</li>
                      <li><strong>Durée, étapes, rythme</strong> (voyage itinérant ? séjour fixe ? combien de kilomètres à pied chaque jour ?)</li>
                      <li><strong>Frais en cas d'annulation</strong> (20 % / 50 % / 100 % selon la date — voir plus bas)</li>
                      <li><strong>Formalités spécifiques</strong> (visa, ESTA, vaccins recommandés…)</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-xl p-5 border-l-4 border-[#5BC5D4]">
                <div className="w-10 h-10 rounded-full bg-[#5BC5D4]/10 flex items-center justify-center text-xl font-black text-[#5BC5D4] mb-2">F</div>
                <h4 className="text-base font-bold text-[#1E3A5F] mb-1">Famille</h4>
                <p className="text-sm text-gray-600">{<TexteRiche texte={T('ip_vfast_famille')} />}</p>
              </div>
              <div className="bg-white border rounded-xl p-5 border-l-4 border-[#79c9ea]">
                <div className="w-10 h-10 rounded-full bg-[#79c9ea]/10 flex items-center justify-center text-xl font-black text-[#79c9ea] mb-2">A</div>
                <h4 className="text-base font-bold text-[#1E3A5F] mb-1">Argent</h4>
                <p className="text-sm text-gray-600">{<TexteRiche texte={T('ip_vfast_argent')} />}</p>
              </div>
              <div className="bg-white border rounded-xl p-5 border-l-4 border-[#27AE60]">
                <div className="w-10 h-10 rounded-full bg-[#27AE60]/10 flex items-center justify-center text-xl font-black text-[#27AE60] mb-2">S</div>
                <h4 className="text-base font-bold text-[#1E3A5F] mb-1">Santé</h4>
                <p className="text-sm text-gray-600">{<TexteRiche texte={T('ip_vfast_sante')} />}</p>
              </div>
              <div className="bg-white border rounded-xl p-5 border-l-4 border-[#E74C3C]">
                <div className="w-10 h-10 rounded-full bg-[#E74C3C]/10 flex items-center justify-center text-xl font-black text-[#E74C3C] mb-2">T</div>
                <h4 className="text-base font-bold text-[#1E3A5F] mb-1">Travail</h4>
                <p className="text-sm text-gray-600">{<TexteRiche texte={T('ip_vfast_travail')} />}</p>
              </div>
            </div>
            <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-3 text-xs text-gray-700 leading-relaxed text-center">
              <p>{<TexteRiche texte={T('ip_vfast_fin')} />}</p>
            </div>
          </div>
        </section>

        {/* FRAIS D'ANNULATION */}
        <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" id="section-frais-annulation" data-testid="section-frais-annulation">
          <SectionTitle color="bg-red-600">Frais d'annulation</SectionTitle>
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
              <p><strong>Pourquoi des frais d'annulation ?</strong></p>
              <p className="mt-1">{<TexteRiche texte={T('ip_annulation_pourquoi')} />}</p>
              <p className="mt-2 text-xs text-gray-500">L'assurance annulation Groupama peut prendre en charge ces frais selon le motif (maladie, accident, etc.).</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs flex-shrink-0">20%</div>
                <div>
                  <h4 className="font-bold text-sm text-gray-800">Plus de 180 jours avant le départ</h4>
                  <p className="text-xs text-gray-600 mt-0.5">Retenue de <strong>20 % du forfait</strong> + adhésion + assurance</p>
                  <p className="text-xs text-gray-400 mt-0.5">Seul l'acompte initial versé à nos prestataires est retenu.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs flex-shrink-0">50%</div>
                <div>
                  <h4 className="font-bold text-sm text-gray-800">De 180 à 46 jours avant le départ</h4>
                  <p className="text-xs text-gray-600 mt-0.5">Retenue de <strong>50 % du forfait</strong> + adhésion + assurance</p>
                  <p className="text-xs text-gray-400 mt-0.5">La moitié des prestations a déjà été réglée à nos partenaires.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs flex-shrink-0">100%</div>
                <div>
                  <h4 className="font-bold text-sm text-gray-800">Moins de 45 jours avant le départ</h4>
                  <p className="text-xs text-gray-600 mt-0.5">Retenue de <strong>100 % du forfait</strong> + adhésion + assurance</p>
                  <p className="text-xs text-gray-400 mt-0.5">L'intégralité du voyage a été réglée aux prestataires à cette date.</p>
                </div>
              </div>
            </div>
            <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-3 text-xs text-gray-700 leading-relaxed">
              <p>{<TexteRiche texte={T('ip_annulation_logique')} />}</p>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section className="bg-gradient-to-br from-[#1E3A5F] to-[#2A4A6F] rounded-2xl shadow-md overflow-hidden mb-6 text-white p-8 text-center" data-testid="section-contact-info">
          <h2 className="font-bold text-lg mb-2">Des questions sur l'inscription ?</h2>
          <p className="text-sm text-white/80 mb-5">Notre équipe est là pour vous aider !</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-[#1DA851] transition">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              WhatsApp
            </a>
            <a href="mailto:rhoneamitie@gmail.com" data-testid="contact-email"
              className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-white/30 transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              rhoneamitie@gmail.com
            </a>
          </div>
        </section>

        {/* CTA S'inscrire */}
        <div className="text-center">
          <Link to="/inscription" data-testid="cta-inscrire"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white px-8 py-4 rounded-xl font-bold text-base hover:shadow-xl transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            S'inscrire maintenant
          </Link>
        </div>

      </div>
    </div>
  );
};

export default InformationsAdministratives;
